/**
 * RFC 0036: Agent Chat Component
 *
 * A chat interface for interacting with the backend orchestrator.
 * Reuses existing chat components (ChatMessage, ChatInput, QuestionCard).
 *
 * Features:
 * - Status-aware send button (idle/running/polling/pending_questions)
 * - Dismissable error card above input
 * - Question option cards for user responses
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import type { Node as ReactFlowNode } from 'reactflow'
import Markdown from 'react-markdown'
import { createDebug } from '../../utils/debugLog'

const log = createDebug('dw:chat:ui')
import remarkGfm from 'remark-gfm'
import { listenAppEvent } from '../canvas-v2/nodes/shared/useNodeActions'
import { useAgentChat, ChatMessage as AgentChatMessage, EmbeddedQuestion, AnswerValue } from '../../hooks/useAgentChat'
import { ChatInput, ChatAttachment, type ChatInputHandle } from '../chat/ChatInput'
import { QuestionCard } from '../chat/QuestionCard'
import { ExecutionPlanCard } from '../chat/ExecutionPlanCard'
import { CollapsibleCodeBlock } from '../chat/CollapsibleCodeBlock'
import { DirectorThought } from '../ui/DirectorThought'
import { nodeSchemas } from '../canvas-v2/nodeRegistry'
import {
  InlineMentionComposer,
  type InlineMentionComposerHandle,
  type InlineMentionComposerState,
} from '../chat/InlineMentionComposer'

function getAnswerKey(question: EmbeddedQuestion): string {
  return question.tool_use_id
}

/**
 * Check if an embedded question is unanswered.
 * Valid answers are always non-empty strings.
 */
function isUnanswered(q: EmbeddedQuestion): boolean {
  return !q.answer
}

/**
 * Get embedded questions that need user response from a message.
 */
function getUnansweredQuestions(msg: AgentChatMessage): EmbeddedQuestion[] {
  if (!msg.questions) return []
  return msg.questions.filter(isUnanswered)
}

function getNodeReferenceMeta(nodeId: string, nodes?: ReactFlowNode[]) {
  const node = nodes?.find((candidate) => candidate.id === nodeId)
  if (!node) {
    return {
      label: nodeId,
      icon: '',
    }
  }

  const data = node.data as Record<string, unknown> | undefined
  for (const key of ['name', 'character_name', 'location_name', 'in_name']) {
    const value = data?.[key]
    if (typeof value === 'string' && value.trim()) {
      return {
        label: value.trim(),
        icon: (nodeSchemas[node.type as keyof typeof nodeSchemas] as { icon?: string } | undefined)?.icon ?? '',
      }
    }
  }

  return {
    label: (nodeSchemas[node.type as keyof typeof nodeSchemas] as { label?: string } | undefined)?.label ?? nodeId,
    icon: (nodeSchemas[node.type as keyof typeof nodeSchemas] as { icon?: string } | undefined)?.icon ?? '',
  }
}

// Custom components for react-markdown styling
const markdownComponents = {
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc pl-4 my-1.5 space-y-0.5">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal pl-4 my-1.5 space-y-0.5">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="text-text-secondary">{children}</li>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="my-1.5">{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-medium text-text-primary">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em className="text-text-secondary">{children}</em>
  ),
  code: ({ children, className }: { children?: React.ReactNode; className?: string }) => {
    const isInline = !className?.includes('language-')
    if (isInline) {
      return (
        <code className="bg-surface-1 px-1 py-0.5 rounded text-xs font-mono text-text-primary">{children}</code>
      )
    }
    return <>{children}</>
  },
  pre: ({ children }: { children?: React.ReactNode }) => {
    const codeElement = children as React.ReactElement
    const className = codeElement?.props?.className || ''
    const match = /language-(\w+)/.exec(className)
    return (
      <CollapsibleCodeBlock language={match?.[1]}>
        {children}
      </CollapsibleCodeBlock>
    )
  },
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-base font-medium text-text-primary mt-2 mb-1">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-sm font-medium text-text-primary mt-2 mb-1">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-sm font-medium text-text-primary mt-1.5 mb-0.5">{children}</h3>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} className="text-text-secondary underline underline-offset-2 hover:text-text-primary" target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l border-border pl-2 my-1.5 text-text-muted">{children}</blockquote>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="bg-panel">{children}</thead>
  ),
  tbody: ({ children }: { children?: React.ReactNode }) => (
    <tbody className="divide-y divide-border-subtle">{children}</tbody>
  ),
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr className="border-b border-border-subtle">{children}</tr>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-3 py-2 text-left font-semibold text-text-secondary border border-border">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="px-3 py-2 font-normal text-text-secondary border border-border-subtle">{children}</td>
  ),
}

/** A prompt queued while the agent is still running */
interface QueuedPrompt {
  message: string
  attachments?: ChatAttachment[]
  referencedNodeIds?: string[]
}

interface AgentChatProps {
  projectId: string
  initialPrompt?: string
  /** Visual style for generated assets (e.g., 'cinematic', 'anime') */
  style?: string
  /** Production target ('webtoon' or 'video') */
  productionTarget?: string
  className?: string
  /** Callback when execution is stopped */
  onStop?: () => void
  /** Auto-confirm cost estimation prompts without user interaction */
  skipCostConfirmation?: boolean
  /** Canvas nodes for @mention autocomplete in the chat input */
  canvasNodes?: ReactFlowNode[]
  /** Running non-director node count from YDoc (includes canvas-hidden nodes) */
  ydocRunningCount?: number
  /** Queued (pending_execution) non-director node count from YDoc */
  ydocQueuedCount?: number
  /** Called when a mention pill is clicked — focuses the canvas on that node */
  onMentionClick?: (nodeId: string) => void
  /** Callback when user selects a file to upload from Director chat */
  onUploadFile?: (file: File, type: 'image' | 'video' | 'audio') => void
}

export function AgentChat({
  projectId,
  initialPrompt,
  style,
  productionTarget,
  className = '',
  onStop,
  skipCostConfirmation = true,
  canvasNodes,
  ydocRunningCount = 0,
  ydocQueuedCount = 0,
  onMentionClick,
  onUploadFile,
}: AgentChatProps) {
  // Memoize options to avoid re-renders
  const options = useMemo(
    () => ({ style, productionTarget }),
    [style, productionTarget],
  )

  const {
    messages,
    firstUnansweredMessageIndex,
    status,
    error,
    isLoading,
    startWithPrompt,
    respondToQuestions,
    dismissPendingQuestions,
    deleteMessage,
    editMessage,
    stop,
    awaitingDirector,
  } = useAgentChat(projectId, options)

  // Combined stop handler that calls hook's stop and parent callback
  const handleStop = useCallback(() => {
    stop()
    onStop?.()
  }, [stop, onStop])

  const runningTaskCount = ydocRunningCount
  const queuedTaskCount = ydocQueuedCount
  // Debug: log when running task card visibility changes
  const prevRunningRef = useRef(0)
  if (runningTaskCount !== prevRunningRef.current) {
    log('runningTasks %d → %d (queued: %d)', prevRunningRef.current, runningTaskCount, queuedTaskCount)
    prevRunningRef.current = runningTaskCount
  }

  // Compute display messages: show up to and including first message with unanswered questions
  // This implements sequential display - user sees messages one-by-one, pausing at questions
  // Uses firstUnansweredMessageIndex from backend instead of computing locally
  const { displayMessages, currentQuestions } = useMemo(() => {
    const slice = firstUnansweredMessageIndex === -1
      ? messages
      : messages.slice(0, firstUnansweredMessageIndex + 1)

    // Hide noise from auto-confirm flows:
    // - Assistant messages whose only questions are answered cost_confirmations
    //   AND have no textual content worth showing
    // - User "continue"/"confirm" bubbles that immediately follow such messages
    const isAutoAcceptedAssistant = (msg: AgentChatMessage) => {
      if (msg.role !== 'assistant' || !msg.questions?.length) return false
      return msg.questions.every((q) => {
        if (q.answer == null) return false
        if (q.tool_use_id === '__cost_confirmation__') return true
        return false
      })
    }

    // Check if a message is an assistant with answered director questions
    // (answers now shown inline on the card, so the echo user bubble is redundant)
    const hasAnsweredDirectorQuestions = (msg: AgentChatMessage) => {
      if (msg.role !== 'assistant' || !msg.questions?.length) return false
      return msg.questions.some(
        (q) => q.question_type === 'director' && q.answer && q.answer !== '__skipped__',
      )
    }

    const filtered = slice.filter((msg, i, arr) => {
      // Hide auto-accepted assistant messages with no content, UNLESS they
      // have an answered cost confirmation with metadata (ExecutionPlanCard).
      if (isAutoAcceptedAssistant(msg) && !msg.content?.trim()) {
        const hasPersistableCard = msg.questions?.some(
          (q) => q.tool_use_id === '__cost_confirmation__' && q.metadata?.nodes && q.answer,
        )
        if (!hasPersistableCard) return false
      }
      if (msg.role === 'user') {
        const prev = arr[i - 1]
        // Hide "continue"/"confirm" echoes after auto-accepted messages
        if ((msg.content === 'continue' || msg.content === 'confirm') && prev && isAutoAcceptedAssistant(prev) && !prev.content?.trim()) {
          return false
        }
        // Hide user answer echo after assistant message with answered questions
        // (the answer is shown inline on the QuestionCard/ExecutionPlanCard)
        if (prev && hasAnsweredDirectorQuestions(prev)) {
          const answeredTexts = prev.questions!
            .filter((q) => q.question_type === 'director' && q.answer && q.answer !== '__skipped__')
            .map((q) => q.answer!.replace(/^User selected: /, ''))
          const userContent = msg.content?.trim()
          if (userContent) {
            // Backend formats as "answer1; answer2" — check both individual and joined forms
            if (
              answeredTexts.some((a) => userContent === a || userContent.includes(a) || a.includes(userContent)) ||
              userContent === answeredTexts.join('; ')
            ) {
              return false
            }
          }
        }
      }
      return true
    })

    const currentQuestions: EmbeddedQuestion[] = firstUnansweredMessageIndex === -1
      ? []
      : getUnansweredQuestions(messages[firstUnansweredMessageIndex])

    // Cost confirmation questions are non-gating (firstUnansweredMessageIndex
    // skips them) so they won't appear in currentQuestions above. Collect them
    // separately from ALL visible messages so the ExecutionPlanCard renders.
    const costQuestions: EmbeddedQuestion[] = []
    for (const msg of filtered) {
      if (!msg.questions) continue
      for (const q of msg.questions) {
        if (!q.answer && (q.tool_use_id === '__cost_confirmation__' || q.tool_use_id === '__insufficient_funds__')) {
          costQuestions.push(q)
        }
      }
    }

    return { displayMessages: filtered, currentQuestions: [...currentQuestions, ...costQuestions] }
  }, [messages, firstUnansweredMessageIndex])

  // Questions from embedded messages (no more legacy pending questions)
  const effectiveQuestions: EmbeddedQuestion[] = currentQuestions

  const [queuedPrompts, setQueuedPrompts] = useState<QueuedPrompt[]>([])

  // RFC-0289: Track whether the Director is actively thinking/streaming.
  // Uses `awaitingDirector` from the hook (set on send, cleared on first
  // stream chunk / done / error) instead of local state, which avoids
  // flicker when SSE events arrive with brief gaps.

  const isDirectorActive = useMemo(() => {
    if (status !== 'running') return false
    // Director is active if any message is currently streaming
    if (displayMessages.some(m => m.isStreaming)) return true
    // Or if we just sent a message and are waiting for the director to respond
    return awaitingDirector
  }, [status, displayMessages, awaitingDirector])
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({})
  const [customTexts, setCustomTexts] = useState<Record<string, string>>({})  // RFC 0053
  const [dismissedError, setDismissedError] = useState(false)
  const [submittingQuestionIds, setSubmittingQuestionIds] = useState<Set<string>>(new Set())
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false)
  const isUserScrolledUpRef = useRef(false)
  const isProgrammaticScrollRef = useRef(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const questionsStartRef = useRef<HTMLDivElement>(null)
  const questionRefs = useRef<HTMLDivElement[]>([])
  const hasStartedRef = useRef(false)
  const initialScrollDoneRef = useRef(false)
  const prevMessageCountRef = useRef(0)
  const prevStatusRef = useRef(status)
  const scrollThrottleRef = useRef(0)

  // Programmatic scroll helper — marks the scroll as non-user so handleScroll
  // won't accidentally re-enable auto-scroll.
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    isProgrammaticScrollRef.current = true
    messagesEndRef.current?.scrollIntoView({ behavior })
    setTimeout(() => { isProgrammaticScrollRef.current = false }, 150)
  }, [])

  // Detect user scroll position to pause/resume auto-scroll.
  // Ignores programmatic scrolls (scrollToBottom) so only real user
  // gestures toggle the flag.
  const handleScroll = useCallback(() => {
    if (isProgrammaticScrollRef.current) return
    const now = Date.now()
    if (now - scrollThrottleRef.current < 100) return
    scrollThrottleRef.current = now
    const el = scrollContainerRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    const scrolledUp = distanceFromBottom > 50
    isUserScrolledUpRef.current = scrolledUp
    setIsUserScrolledUp(scrolledUp)
  }, [])

  // Attach scroll listener to the scroll container
  // Re-run when isLoading changes so we attach after the loading spinner is replaced
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [handleScroll, isLoading])

  // Auto-scroll when messages change
  useEffect(() => {
    const isInitialLoad = !initialScrollDoneRef.current && messages.length > 0
    const hasNewMessages = messages.length > prevMessageCountRef.current

    if (isInitialLoad) {
      scrollToBottom('instant')
      initialScrollDoneRef.current = true
    } else if (hasNewMessages && !isUserScrolledUpRef.current) {
      scrollToBottom()
    }

    prevMessageCountRef.current = messages.length
  }, [messages, scrollToBottom])

  // Auto-scroll during streaming (throttled to avoid jank)
  const lastStreamScrollRef = useRef(0)
  useEffect(() => {
    if (isUserScrolledUpRef.current) return
    const lastMsg = displayMessages[displayMessages.length - 1]
    if (lastMsg?.isStreaming && lastMsg.content) {
      const now = Date.now()
      if (now - lastStreamScrollRef.current > 300) {
        lastStreamScrollRef.current = now
        scrollToBottom()
      }
    }
  }, [displayMessages, scrollToBottom])

  // Scroll to show questions when they appear
  useEffect(() => {
    if (!isUserScrolledUpRef.current && effectiveQuestions.length > 0) {
      scrollToBottom()
    }
  }, [effectiveQuestions.length, scrollToBottom])

  // Auto-start with initial prompt if provided
  // Only send if conversation is empty (prevents duplicate on page refresh)
  useEffect(() => {
    if (initialPrompt && !hasStartedRef.current && !isLoading && status === 'idle' && messages.length === 0) {
      hasStartedRef.current = true
      startWithPrompt(initialPrompt)
      // Clear location.state so refresh doesn't re-send
      window.history.replaceState({}, document.title)
    }
  }, [initialPrompt, isLoading, status, startWithPrompt, messages.length])

  // Dequeue: when status transitions from running → idle and queue is non-empty,
  // pop the first queued prompt and send it automatically
  useEffect(() => {
    const wasRunning = prevStatusRef.current === 'running'
    prevStatusRef.current = status

    if (wasRunning && status === 'idle' && queuedPrompts.length > 0) {
      const [next, ...rest] = queuedPrompts
      setQueuedPrompts(rest)
      startWithPrompt(next.message, next.referencedNodeIds)
    }
  }, [status, queuedPrompts, startWithPrompt])

  // Reset dismissed error when a new error occurs
  useEffect(() => {
    if (error) {
      setDismissedError(false)
    }
  }, [error])

  // Clear submitting state and stale selections when questions change
  // (e.g. director errored and came back with the same question)
  useEffect(() => {
    setSubmittingQuestionIds(new Set())
    setSelectedAnswers({})
    setCustomTexts({})
  }, [effectiveQuestions])


  // Auto-confirm cost estimation questions when enabled
  useEffect(() => {
    if (!skipCostConfirmation) return
    if (effectiveQuestions.length === 0) return

    const costQuestions = effectiveQuestions.filter(
      (q) => q.tool_use_id === '__cost_confirmation__'
    )
    if (costQuestions.length === 0) return

    const answers: Record<string, AnswerValue> = {}
    for (const question of costQuestions) {
      answers[getAnswerKey(question)] = { selected_option: 'confirm', custom_text: 'cost auto confirmed' }
    }
    if (Object.keys(answers).length > 0) {
      respondToQuestions(answers)
    }
  }, [skipCostConfirmation, effectiveQuestions, respondToQuestions])

  // Listen for story structure events from canvas
  useEffect(() => {
    // Legacy handler for storyStructure node (if still used)
    const handleGenerateScenes = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (status === 'running') return

      const cards = detail.cards || []
      const cardSummary = cards
        .map((card: { sceneNumber: number; purpose: string; storyBeat: string }) => 
          `Scene ${card.sceneNumber} (${card.purpose}): ${card.storyBeat}`)
        .join('\n')

      const message = `Generate scenes from my story structure:\n\nTone: ${detail.tone || 'Cinematic'}\nHook: ${detail.hook?.text || 'N/A'}\n\nStructure:\n${cardSummary}`
      
      startWithPrompt(message)
    }

    const handleCardAction = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (status === 'running') return

      const actionMap: Record<string, string> = {
        'regen': 'Please regenerate',
        'shorter': 'Please make this shorter',
        'expand': 'Please expand with more detail',
      }
      const actionText = actionMap[detail.action] || detail.action

      const message = `${actionText} for Scene ${detail.card?.sceneNumber} (${detail.card?.purpose}):\n\n"${detail.card?.storyBeat}"\n\nContext: ${detail.context?.tone || 'Cinematic'} tone`
      
      startWithPrompt(message)
    }

    window.addEventListener('generate-scenes-from-structure', handleGenerateScenes)
    window.addEventListener('story-structure:card-action', handleCardAction)

    return () => {
      window.removeEventListener('generate-scenes-from-structure', handleGenerateScenes)
      window.removeEventListener('story-structure:card-action', handleCardAction)
    }
  }, [status, startWithPrompt])

  // Listen for agent-chat:send events (e.g., from story direction approval)
  useEffect(() => {
    const handleChatSend = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (status === 'running') return
      if (detail?.message) {
        startWithPrompt(detail.message)
        // Notify the story direction node that approval was triggered via chat
        window.dispatchEvent(new CustomEvent('story-direction:approved-via-chat'))
      }
    }
    window.addEventListener('agent-chat:send', handleChatSend)
    return () => window.removeEventListener('agent-chat:send', handleChatSend)
  }, [status, startWithPrompt])

  // Listen for add-node-to-chat events (e.g., from node toolbar "Add to Chat" button)
  useEffect(() => {
    return listenAppEvent('add-node-to-chat', (detail) => {
      if (detail?.nodeId) {
        // Small delay to let sidebar open animation complete
        setTimeout(() => {
          chatInputRef.current?.addMention(detail.nodeId)
        }, 100)
      }
    })
  }, [])

  const handleOptionSelect = useCallback((questionId: string, option: string) => {
    const question = effectiveQuestions.find(q => getAnswerKey(q) === questionId)
    if (!question) return

    // Update local selection immediately for visual feedback
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: option }))
    // Clear custom text so the clicked option takes priority
    setCustomTexts((prev) => ({ ...prev, [questionId]: '' }))

    // Check if ALL questions will be answered after this selection
    const updatedAnswers = { ...selectedAnswers, [questionId]: option }
    const allAnswered = effectiveQuestions.every(q => {
      const key = getAnswerKey(q)
      return !!updatedAnswers[key] || !!customTexts[key]?.trim()
    })

    // Only auto-submit if ALL questions are now answered
    if (allAnswered) {
      setTimeout(() => {
        // Mark all questions as submitting
        setSubmittingQuestionIds(prev => {
          const next = new Set(prev)
          effectiveQuestions.forEach(q => next.add(getAnswerKey(q)))
          return next
        })
        
        // Build answers for ALL questions and submit together
        const answers: Record<string, AnswerValue> = {}
        for (const q of effectiveQuestions) {
          const key = getAnswerKey(q)
          const custom = customTexts[key]?.trim()
          if (custom) {
            answers[key] = { selected_option: 'user_feedback', custom_text: custom }
          } else {
            answers[key] = { selected_option: updatedAnswers[key] }
          }
        }
        
        respondToQuestions(answers)
      }, 150)
    }
  }, [effectiveQuestions, respondToQuestions, selectedAnswers, customTexts])

  // RFC 0053: Handle custom text change (mutually exclusive with selection)
  const handleCustomTextChange = useCallback((questionId: string, text: string) => {
    setCustomTexts((prev) => ({ ...prev, [questionId]: text }))
  }, [])

  // RFC 0053: Clear selection when user focuses on custom input (mutually exclusive)
  const handleCustomInputFocus = useCallback((questionId: string) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: '' }))
  }, [])

  // Submit custom text on Enter key
  const handleCustomSubmit = useCallback((questionId: string) => {
    const custom = customTexts[questionId]?.trim()
    if (!custom) return

    setSubmittingQuestionIds(prev => {
      const next = new Set(prev)
      effectiveQuestions.forEach(q => next.add(getAnswerKey(q)))
      return next
    })

    const answers: Record<string, AnswerValue> = {}
    for (const q of effectiveQuestions) {
      const key = getAnswerKey(q)
      if (key === questionId) {
        answers[key] = { selected_option: 'user_feedback', custom_text: custom }
      } else {
        const sel = selectedAnswers[key]
        const ct = customTexts[key]?.trim()
        if (ct) {
          answers[key] = { selected_option: 'user_feedback', custom_text: ct }
        } else if (sel) {
          answers[key] = { selected_option: sel }
        }
      }
    }
    respondToQuestions(answers)
  }, [effectiveQuestions, respondToQuestions, selectedAnswers, customTexts])

  const chatInputRef = useRef<ChatInputHandle>(null)

  const handleSendMessage = useCallback((message: string, attachments?: ChatAttachment[], referencedNodeIds?: string[]) => {
    if (!message.trim() && (!attachments || attachments.length === 0)) return

    // RFC-0289: Send directly — the backend accepts messages during "running"
    // state and serialises via the Director lock.  No more local queueing.
    isUserScrolledUpRef.current = false
    setIsUserScrolledUp(false)
    setDismissedError(true)
    if (effectiveQuestions.length > 0) {
      dismissPendingQuestions()
      setSelectedAnswers({})
      setCustomTexts({})
    }
    startWithPrompt(message, referencedNodeIds)
  }, [startWithPrompt, effectiveQuestions, dismissPendingQuestions])

  const removeQueuedPrompt = useCallback((index: number) => {
    setQueuedPrompts((prev) => prev.filter((_, i) => i !== index))
  }, [])

  // Move a queued prompt back to the text field for editing
  const editQueuedPrompt = useCallback((index: number) => {
    setQueuedPrompts((prev) => {
      const item = prev[index]
      if (item) {
        chatInputRef.current?.setText(item.message)
      }
      return prev.filter((_, i) => i !== index)
    })
  }, [])

  // Send a queued prompt immediately, skipping any pending questions.
  const sendQueuedPrompt = useCallback((index: number) => {
    const item = queuedPrompts[index]
    if (!item) return
    setQueuedPrompts((prev) => prev.filter((_, i) => i !== index))
    setDismissedError(true)
    if (effectiveQuestions.length > 0) {
      dismissPendingQuestions()
      setSelectedAnswers({})
      setCustomTexts({})
    }
    startWithPrompt(item.message, item.referencedNodeIds)
  }, [queuedPrompts, startWithPrompt, effectiveQuestions, dismissPendingQuestions])

  // Check if there are real (non-system) questions that require user response
  // System prompts start with __ and don't block sending
  const hasRealQuestions = effectiveQuestions.length > 0 &&
    effectiveQuestions.some(q => !getAnswerKey(q).startsWith('__'))

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      {/* Messages wrapper - relative for floating scroll button */}
      <div className="flex-1 relative overflow-hidden">
        {/* Messages - display up to first unanswered question */}
        <div ref={scrollContainerRef} data-testid="chat-scroll-container" className="h-full overflow-y-auto overflow-x-hidden px-4 py-4 space-y-4 focus-visible:ring-0 focus-visible:ring-offset-0">
          {displayMessages.map((message, index) => {
            if (message.isSystemNotification) {
            return (
              <div key={index} className="flex items-center justify-center gap-1.5 py-1">
                <svg className="w-3 h-3 text-text-muted/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span className="text-xs text-text-muted/50">{message.content}</span>
              </div>
            )
          }
          // Hide Director header when previous non-system message was also from the Director
          const prevMsg = displayMessages.slice(0, index).reverse().find(m => !m.isSystemNotification)
          const showHeader = message.role !== 'assistant' || !prevMsg || prevMsg.role !== 'assistant'
          return (
            <MessageBubble
              key={index}
              message={message}
              canvasNodes={canvasNodes}
              onMentionClick={onMentionClick}
              onDelete={status !== 'running' ? () => deleteMessage(message.timestamp, message.content) : undefined}
              onEdit={status !== 'running' && message.role === 'user' ? (newContent: string, referencedNodeIds: string[]) => editMessage(message.timestamp, message.content, newContent, referencedNodeIds) : undefined}
              showHeader={showHeader}
            />
          )
        })}

        {/* Current questions (embedded from messages) */}
        {effectiveQuestions.length > 0 && <div ref={questionsStartRef} />}
        {effectiveQuestions.map((question, index) => {
          const key = getAnswerKey(question)
          const isSubmitting = submittingQuestionIds.has(key)
          const isMultiQuestion = effectiveQuestions.length > 1

          // Cost confirmation: render ExecutionPlanCard with node details
          if (question.tool_use_id === '__cost_confirmation__' && question.metadata?.nodes) {
            const nodes = question.metadata.nodes as Array<{ node_id: string; type: string; label: string; sparks: number }>
            const totalSparks = (question.metadata.total_sparks as number) ?? 0
            return (
              <ExecutionPlanCard
                key={key}
                nodes={nodes}
                totalSparks={totalSparks}
                selectedOption={selectedAnswers[key]}
                onSelect={(option) => handleOptionSelect(key, option)}
                onNodeClick={onMentionClick}
                disabled={isSubmitting}
              />
            )
          }

          return (
            <QuestionCard
              key={key}
              ref={(el) => {
                if (el) questionRefs.current[index] = el
              }}
              question={question.question}
              options={question.options}
              selectedOption={selectedAnswers[key]}
              onSelect={(option) => handleOptionSelect(key, option)}
              disabled={isSubmitting}
              allowCustomInput={question.allow_custom_input}
              customText={customTexts[key] || ''}
              onCustomTextChange={(text) => handleCustomTextChange(key, text)}
              onCustomInputFocus={() => handleCustomInputFocus(key)}
              onCustomSubmit={() => handleCustomSubmit(key)}
              autoSubmit={true}
              isMultiQuestion={isMultiQuestion}
              questionIndex={index}
              onSelectionMade={() => {
                // Scroll to next question if there is one
                if (index < effectiveQuestions.length - 1) {
                  setTimeout(() => {
                    questionRefs.current[index + 1]?.scrollIntoView({ 
                      behavior: 'smooth', 
                      block: 'center' 
                    })
                  }, 200)
                }
              }}
            />
          )
        })}

        {/* Running indicator — only when Director is actively thinking/streaming,
            not when only worker nodes are generating (RFC-0289) */}
        {isDirectorActive && effectiveQuestions.length === 0 && (
          <div className="text-base font-medium">
            <DirectorThought />
          </div>
        )}

        <div ref={messagesEndRef} />
        </div>

        {/* Floating scroll-to-bottom button */}
        {isUserScrolledUp && (
          <button
            type="button"
            data-testid="scroll-to-bottom-button"
            onClick={() => {
              isUserScrolledUpRef.current = false
              setIsUserScrolledUp(false)
              scrollToBottom()
            }}
            className="absolute bottom-3 right-4 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-surface-secondary/80 border border-white/10 shadow-lg backdrop-blur-sm text-text-muted hover:text-text-primary hover:bg-surface-secondary transition-all duration-200"
            title="Scroll to bottom"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        )}
      </div>

      {/* Running/queued tasks indicator — shows count of non-director nodes currently executing or queued */}
      {(runningTaskCount > 0 || queuedTaskCount > 0) && (
        <div
          data-testid="running-tasks-card"
          className="mx-3 mb-2 p-3 bg-primary/10 border border-primary/30 rounded-md"
        >
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            <span className="text-sm text-primary">
              {runningTaskCount > 0 && queuedTaskCount > 0
                ? `${runningTaskCount} running, ${queuedTaskCount} queued`
                : runningTaskCount > 0
                  ? `${runningTaskCount} task${runningTaskCount > 1 ? 's' : ''} running`
                  : `${queuedTaskCount} task${queuedTaskCount > 1 ? 's' : ''} queued`}
            </span>
          </div>
        </div>
      )}

      {/* Error card - show whenever there's an error, regardless of status */}
      {error && !dismissedError && (
        <div className="mx-3 mb-2 p-3 bg-red-500/20 border border-red-500/50 rounded-md">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1 text-base text-red-400">{error}</div>
            <button
              onClick={() => setDismissedError(true)}
              className="text-red-400/60 hover:text-red-400 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Queued prompts stack — shown between error card and input */}
      {queuedPrompts.length > 0 && (
        <div className="mx-3 mb-2 space-y-1.5" data-testid="queued-prompts-stack">
          <div className="text-[11px] font-medium text-text-muted uppercase tracking-wider">
            Queued ({queuedPrompts.length})
          </div>
          {queuedPrompts.map((qp, i) => (
            <div
              key={i}
              data-testid={`queued-prompt-${i}`}
              className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-[13px] text-text-secondary"
            >
              <span className="flex-1 truncate">{qp.message}</span>
              <div className="flex-shrink-0 flex items-center gap-0.5">
                <button
                  type="button"
                  data-testid={`queued-prompt-edit-${i}`}
                  onClick={() => editQueuedPrompt(i)}
                  className="p-1 rounded hover:bg-white/10 text-text-muted hover:text-text-primary transition-colors"
                  title="Edit in text field"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  data-testid={`queued-prompt-send-${i}`}
                  onClick={() => sendQueuedPrompt(i)}
                  disabled={false}
                  className="p-1 rounded transition-colors hover:bg-primary/20 text-text-muted hover:text-primary"
                  title="Send now (skips pending questions)"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  data-testid={`queued-prompt-remove-${i}`}
                  onClick={() => removeQueuedPrompt(i)}
                  className="p-1 rounded hover:bg-destructive/20 text-text-muted hover:text-destructive transition-colors"
                  title="Delete queued message"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Chat input with status indicator */}
      {/* RFC 0061: Added onStop for stop button */}
      <ChatInput
        ref={chatInputRef}
        onSend={handleSendMessage}
        onStop={handleStop}
        projectId={projectId}
        canvasNodes={canvasNodes}
        onMentionClick={onMentionClick}
        onUploadFile={onUploadFile}
        placeholder={
          hasRealQuestions
            ? 'Type to skip questions and continue...'
            : isDirectorActive
              ? 'Send a message...'
              : error
                ? 'Type a message to retry...'
                : messages.length > 0
                  ? 'Type to continue the conversation...'
                  : 'Type a message to start...'
        }
        status={isDirectorActive && !hasRealQuestions ? 'running' : 'idle'}
      />
    </div>
  )
}

/** Strip ```dag-proposal, ```story-structure, and ```story-direction code blocks from message content so users don't see raw JSON. */
function stripInternalBlocks(content: string): string {
  return content
    .replace(/```dag-proposal[\s\S]*?```/g, '')
    .replace(/```story-structure[\s\S]*?```/g, '')
    .replace(/```story-direction[\s\S]*?```/g, '')
    .trim()
}

/** Clean AI-generated text by replacing em-dashes with more natural alternatives */
function cleanAiText(text: string): string {
  return text
    .replace(/\s*—\s*/g, ', ')  // em-dash with optional spaces -> comma
    .replace(/\s*–\s*/g, ', ')  // en-dash -> comma
}

/** Regex matching inline mention markers like @[[nodeId]] */
const MENTION_MARKER_RE = /@\[\[([^\]]+)\]\]/g

/**
 * Render message content with inline mention chips at their original positions.
 *
 * Splits the content around @[[nodeId]] markers and interleaves plain-text
 * spans with clickable mention chip buttons.  Falls back to null when the
 * content has no markers (caller should use the legacy "chips-first" path).
 */
function renderInlineMentions(
  content: string,
  canvasNodes: ReactFlowNode[] | undefined,
  onMentionClick: ((nodeId: string) => void) | undefined,
): React.ReactNode | null {
  // Reset lastIndex since we reuse the global regex
  MENTION_MARKER_RE.lastIndex = 0
  if (!MENTION_MARKER_RE.test(content)) return null
  MENTION_MARKER_RE.lastIndex = 0

  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = MENTION_MARKER_RE.exec(content)) !== null) {
    // Text before this marker
    if (match.index > lastIndex) {
      parts.push(<span key={`t-${lastIndex}`}>{content.slice(lastIndex, match.index)}</span>)
    }

    const nodeId = match[1]
    const { label, icon } = getNodeReferenceMeta(nodeId, canvasNodes)
    parts.push(
      <button
        key={`m-${nodeId}-${match.index}`}
        type="button"
        data-testid={`chat-message-reference-chip-${nodeId}`}
        onClick={() => onMentionClick?.(nodeId)}
        className="inline-flex items-center gap-1 rounded-md border border-primary/25 bg-primary/15 px-2 py-0.5 text-xs text-primary transition-colors hover:border-primary/40 hover:bg-primary/20 mx-0.5 align-middle"
        title="Focus on canvas"
      >
        {icon && <span>{icon}</span>}
        <span>{label}</span>
      </button>,
    )

    lastIndex = match.index + match[0].length
  }

  // Remaining text after last marker
  if (lastIndex < content.length) {
    parts.push(<span key={`t-${lastIndex}`}>{content.slice(lastIndex)}</span>)
  }

  return <>{parts}</>
}

// Legacy alias for backward compatibility
const stripDagProposals = stripInternalBlocks

// Trash icon for delete button (Lucide Trash2)
function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  )
}

// Pencil icon for edit indicator
function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  )
}

// Message component
function MessageBubble({
  message,
  canvasNodes,
  onMentionClick,
  onDelete,
  onEdit,
  showHeader = true,
}: {
  message: AgentChatMessage
  canvasNodes?: ReactFlowNode[]
  onMentionClick?: (nodeId: string) => void
  onDelete?: () => void
  onEdit?: (newContent: string, referencedNodeIds: string[]) => void
  showHeader?: boolean
}) {
  const isUser = message.role === 'user'
  const [isEditing, setIsEditing] = useState(false)
  const editComposerRef = useRef<InlineMentionComposerHandle>(null)
  const editStateRef = useRef<InlineMentionComposerState>({ plainText: '', referencedNodeIds: [] })

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditing) {
      // Defer focus to after the composer mounts and hydrates
      requestAnimationFrame(() => editComposerRef.current?.focus())
    }
  }, [isEditing])

  // Build initialContent for the edit composer: if the message uses legacy
  // referenced_node_ids (no @[[]] markers in content), prepend markers so the
  // composer can hydrate them into chips.
  const editInitialContent = useMemo(() => {
    const hasMarkers = /@\[\[[^\]]+\]\]/.test(message.content)
    if (hasMarkers || !message.referenced_node_ids?.length) return message.content
    const prefix = message.referenced_node_ids.map((id) => `@[[${id}]]`).join(' ')
    return `${prefix} ${message.content}`
  }, [message.content, message.referenced_node_ids])

  const handleDoubleClick = useCallback(() => {
    if (!onEdit) return
    setIsEditing(true)
  }, [onEdit])

  const handleEditSubmit = useCallback(() => {
    const { plainText, referencedNodeIds } = editStateRef.current
    const trimmed = plainText.trim()
    if (!trimmed || !onEdit) {
      setIsEditing(false)
      return
    }
    setIsEditing(false)
    onEdit(trimmed, referencedNodeIds)
  }, [onEdit])

  const handleEditCancel = useCallback(() => {
    setIsEditing(false)
  }, [])

  const deleteButton = onDelete ? (
    <button
      type="button"
      data-testid="delete-message-button"
      onClick={onDelete}
      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-white/10 text-text-muted hover:text-red-400"
      title="Delete message"
    >
      <TrashIcon className="w-3.5 h-3.5" />
    </button>
  ) : null

  if (isUser) {
    const cleanUserContent = cleanAiText(stripDagProposals(message.content))
    // User messages: bubble style
    return (
      <div className="group flex justify-end gap-1 items-start">
        {deleteButton}
        <div
          className={`relative max-w-[85%] px-3 py-2 text-[15px] leading-relaxed text-text-primary bg-white/10 border border-white/10 rounded-lg rounded-br-sm ${onEdit ? 'cursor-pointer' : ''}`}
          onDoubleClick={handleDoubleClick}
          data-testid="user-message-bubble"
        >
          {isEditing ? (
            <div className="flex flex-col gap-1">
              <div
                data-testid="edit-message-composer"
                className="w-full bg-transparent border border-white/20 rounded text-[15px] leading-relaxed text-text-primary focus-within:border-primary/50"
              >
                <InlineMentionComposer
                  ref={editComposerRef}
                  canvasNodes={canvasNodes}
                  initialContent={editInitialContent}
                  placeholder="Edit message..."
                  onContentChange={(state) => { editStateRef.current = state }}
                  onSubmit={handleEditSubmit}
                  onEscape={handleEditCancel}
                  onMentionClick={onMentionClick}
                />
              </div>
              <div className="flex justify-end gap-1 text-[11px] text-text-muted">
                <span>Enter to save</span>
                <span className="mx-0.5">|</span>
                <span>Esc to cancel</span>
              </div>
            </div>
          ) : (
            <div className="break-words whitespace-pre-wrap text-left">
              {(() => {
                // New path: content contains @[[nodeId]] markers — render chips inline
                const inlineRendered = renderInlineMentions(
                  cleanUserContent,
                  canvasNodes,
                  onMentionClick,
                )
                if (inlineRendered) return inlineRendered

                // Legacy path: no markers in content — render chips first, then text
                return (
                  <>
                    {message.referenced_node_ids && message.referenced_node_ids.length > 0 &&
                      message.referenced_node_ids.map((nodeId) => {
                        const { label, icon } = getNodeReferenceMeta(nodeId, canvasNodes)
                        return (
                          <button
                            key={nodeId}
                            type="button"
                            data-testid={`chat-message-reference-chip-${nodeId}`}
                            onClick={() => onMentionClick?.(nodeId)}
                            className="inline-flex items-center gap-1 rounded-md border border-primary/25 bg-primary/15 px-2 py-0.5 text-xs text-primary transition-colors hover:border-primary/40 hover:bg-primary/20 mr-1.5 mb-0.5 align-middle"
                            title="Focus on canvas"
                          >
                            {icon && <span>{icon}</span>}
                            <span>{label}</span>
                          </button>
                        )
                      })}
                    {cleanUserContent && <span>{cleanUserContent}</span>}
                  </>
                )
              })()}
            </div>
          )}
          {!isEditing && onEdit && (
            <PencilIcon className="absolute bottom-2 right-2 w-3 h-3 text-text-muted opacity-0 group-hover:opacity-50 transition-opacity" />
          )}
        </div>
      </div>
    )
  }

  // Director messages: Georgia serif with typewriter effect
  const cleanContent = cleanAiText(stripDagProposals(message.content))

  return (
    <div className="group space-y-1.5">
      {/* Director label with icon + delete button — hidden for consecutive Director messages */}
      {showHeader && (
        <div className="flex items-center gap-1.5">
          <svg className="w-3 h-3 text-primary" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
          </svg>
          <span className="text-sm font-medium text-text-muted">
            Director
          </span>
          {deleteButton}
        </div>
      )}
      {!showHeader && deleteButton && (
        <div className="flex justify-end">{deleteButton}</div>
      )}

      {/* Message content */}
      <div className="text-[15px] leading-[1.6] text-text-primary break-words">
        <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {cleanContent || ' '}
        </Markdown>
      </div>

      {/* Answered cost confirmation: persist the execution plan card inline */}
      {message.questions?.map((q, qIdx) => {
        if (!q.answer || q.answer === '__skipped__') return null

        // Strip "User selected: " prefix from backend answer format
        const rawAnswer = q.answer.replace(/^User selected: /, '')

        // Cost confirmation → ExecutionPlanCard
        if (q.tool_use_id === '__cost_confirmation__' && q.metadata?.nodes) {
          const nodes = q.metadata.nodes as Array<{ node_id: string; type: string; label: string; sparks: number }>
          const totalSparks = (q.metadata.total_sparks as number) ?? 0
          return (
            <ExecutionPlanCard
              key={q.tool_use_id}
              nodes={nodes}
              totalSparks={totalSparks}
              selectedOption={rawAnswer}
              onSelect={() => {}}
              onNodeClick={onMentionClick}
              disabled
            />
          )
        }

        // All other answered questions → QuestionCard (read-only)
        return (
          <QuestionCard
            key={q.tool_use_id || qIdx}
            question={q.question}
            options={q.options}
            selectedOption={rawAnswer}
            onSelect={() => {}}
            disabled
          />
        )
      })}
    </div>
  )
}

export default AgentChat
